import * as React from "react"
import { Slider as SliderPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

export interface SliderProps extends React.ComponentProps<typeof SliderPrimitive.Root> {
  className?: string
}

/**
 * The standard shadcn Slider over Radix, in the register of the other primitives here: the track
 * is `muted`, the range and the thumb are `foreground`, the thumb's edge is `border`.
 *
 * One deliberate departure from the stock wrapper: `aria-label` and `aria-valuetext` are forwarded
 * to the THUMB. Radix puts `role="slider"` on the thumb and spreads the rest of the props on a
 * role-less root span, so a label left on the root names nothing that assistive tech — or a
 * `getByRole('slider', { name })` query — can find.
 */
function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  "aria-label": ariaLabel,
  "aria-valuetext": ariaValueText,
  ...props
}: SliderProps) {
  const values = React.useMemo(
    () =>
      Array.isArray(value)
        ? value
        : Array.isArray(defaultValue)
          ? defaultValue
          : [min],
    [value, defaultValue, min]
  )

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      className={cn(
        "relative flex w-full touch-none items-center select-none data-[disabled]:opacity-50",
        className
      )}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        className="relative h-1 w-full grow overflow-hidden rounded-full bg-muted"
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          className="absolute h-full bg-foreground"
        />
      </SliderPrimitive.Track>
      {values.map((_, i) => (
        <SliderPrimitive.Thumb
          data-slot="slider-thumb"
          key={i}
          aria-label={ariaLabel}
          aria-valuetext={ariaValueText}
          className="block size-3 shrink-0 rounded-full border border-border bg-foreground transition-[color,box-shadow] outline-none hover:ring-4 hover:ring-foreground/10 focus-visible:ring-4 focus-visible:ring-foreground/20 disabled:pointer-events-none"
        />
      ))}
    </SliderPrimitive.Root>
  )
}

export { Slider }
